import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="bg-white px-4 lg:px-6 py-2.5 dark:bg-gray-800">
      <div className="container mx-auto px-4 lg:px-6 flex flex-wrap justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src="/images/qicino-main-logo.svg"
            alt="Qicino Limited"
            className="h-8 lg:h-10 w-auto"
          />
        </Link>

        {/* Right side content */}
        <div className="flex items-center lg:order-2">
          <div className="hidden lg:flex lg:items-center lg:mr-3">
            <svg
              className="w-4 h-4 text-yellow-300 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">
              Star
            </span>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-4">
              999
            </span>
          </div>

          <button
            onClick={() => setShowLoginModal(true)}
            className="text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 focus:ring-4 focus:ring-orange-300 font-medium rounded-lg text-sm px-4 lg:px-5 py-2 lg:py-2.5 mr-2 dark:focus:ring-orange-800"
          >
            Portal
          </button>

          {/* Mobile menu button */}
          <button
            type="button"
            className="inline-flex items-center p-2 ml-1 text-sm text-gray-500 rounded-lg lg:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="sr-only">Open main menu</span>
            {isOpen ? (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clipRule="evenodd"
                ></path>
              </svg>
            )}
          </button>
        </div>

        {/* Desktop Navigation - Center */}
        <div className="hidden justify-between items-center w-full lg:flex lg:w-auto lg:order-1">
          <ul className="flex flex-col mt-4 font-medium lg:flex-row lg:space-x-8 lg:mt-0">
            <li>
              <Link
                to="/"
                className={`block py-2 pr-4 pl-3 rounded lg:bg-transparent lg:p-0 ${
                  isActive("/")
                    ? "text-primary dark:text-white"
                    : "text-gray-700 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent"
                }`}
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/services"
                className={`block py-2 pr-4 pl-3 rounded lg:bg-transparent lg:p-0 ${
                  isActive("/services")
                    ? "text-primary dark:text-white"
                    : "text-gray-700 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent"
                }`}
              >
                Services
              </Link>
            </li>
            <li>
              <Link
                to="/training"
                className={`block py-2 pr-4 pl-3 rounded lg:bg-transparent lg:p-0 ${
                  isActive("/training")
                    ? "text-primary dark:text-white"
                    : "text-gray-700 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent"
                }`}
              >
                Training
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                className={`block py-2 pr-4 pl-3 rounded lg:bg-transparent lg:p-0 ${
                  isActive("/about")
                    ? "text-primary dark:text-white"
                    : "text-gray-700 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent"
                }`}
              >
                About
              </Link>
            </li>
            <li>
              <a
                href="https://blog.qicino.com"
                className="block py-2 pr-4 pl-3 rounded lg:bg-transparent lg:p-0 text-gray-700 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent"
              >
                Blog
              </a>
            </li>
            <li>
              <Link
                to="/contact"
                className={`block py-2 pr-4 pl-3 rounded lg:bg-transparent lg:p-0 ${
                  isActive("/contact")
                    ? "text-primary dark:text-white"
                    : "text-gray-700 hover:bg-gray-50 lg:hover:bg-transparent lg:border-0 lg:hover:text-primary dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent"
                }`}
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>

        {/* Mobile Sidebar Menu Overlay */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/40 lg:hidden z-[998]"
            onClick={() => setIsOpen(false)}
          />
        )}

        {/* Mobile Sidebar Menu */}
        <div
          className={`fixed top-0 left-0 h-screen w-64 bg-white shadow-lg z-[999] lg:hidden transform transition-transform duration-300 ease-in-out overflow-y-auto ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Close Button */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Menu</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="p-4">
            <ul className="space-y-1">
              <li>
                <Link
                  to="/"
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive("/")
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive("/services")
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/training"
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive("/training")
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Training
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive("/about")
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  About
                </Link>
              </li>
              <li>
                <a
                  href="https://blog.qicino.com"
                  className="block px-4 py-3 rounded-lg font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Blog
                </a>
              </li>
              <li>
                <Link
                  to="/contact"
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive("/contact")
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          {/* Divider */}
          <div className="border-t border-gray-200 mx-4"></div>

          {/* Portal Button */}
          <div className="p-4">
            <button
              onClick={() => {
                setShowLoginModal(true);
                setIsOpen(false);
              }}
              className="w-full text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 font-medium rounded-lg py-2.5 transition-all"
            >
              Staff Portal
            </button>
          </div>
        </div>

        {/* Staff Portal Modal */}
        {showLoginModal && (
          <div className="fixed inset-0 bg-black flex items-center justify-center z-[20000]">
            <div className="bg-white w-full h-full flex flex-col lg:flex-row">
              {/* Close button */}
              <button
                onClick={() => setShowLoginModal(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 z-10"
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Left Side - Company Information */}
              <div className="flex-1 p-8 lg:p-16 flex flex-col justify-center bg-white bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="max-w-lg mx-auto lg:mx-0">
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                    Staff Portal
                  </h2>
                  <p className="text-xl text-gray-600 mb-8">
                    Welcome to Qicino Limited Staff Portal
                  </p>

                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Our Business Ecosystem:
                    </h3>
                    <div className="space-y-4">
                      {/* Software Development - Parent */}
                      <div className="bg-purple-50 border-l-4 border-purple-600 pl-4 py-3 rounded-r-lg">
                        <div className="flex items-start">
                          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <svg
                              className="w-6 h-6 text-purple-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                              />
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900">
                              Software Development
                            </h4>
                            <p className="text-sm text-gray-600">
                              Core Technology Arm (Qicino)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Subsidiaries */}
                      <div className="ml-4 space-y-3 pt-2 border-l-2 border-gray-200 pl-4">
                        {/* Real Estate */}
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <svg
                              className="w-5 h-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                              />
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">
                              Real Estate
                            </h4>
                            <p className="text-xs text-gray-600">
                              Property management & development
                            </p>
                          </div>
                        </div>

                        {/* Automobile */}
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <svg
                              className="w-5 h-5 text-blue-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                              />
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">
                              Automobile
                            </h4>
                            <p className="text-xs text-gray-600">
                              Vehicle sales & services
                            </p>
                          </div>
                        </div>

                        {/* Logistics */}
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                            <svg
                              className="w-5 h-5 text-orange-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                              />
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">
                              Logistics
                            </h4>
                            <p className="text-xs text-gray-600">
                              Supply chain & transportation
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-8">
                    This portal is restricted to authorized staff members only.
                  </p>

                  <button
                    type="button"
                    onClick={() =>
                      window.location.href = "https://portal.qicino.com"
                    }
                    className="w-full lg:w-auto bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-8 rounded-lg hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 font-medium transition-all duration-300 text-lg"
                  >
                    Access Staff Portal
                  </button>

                  <p className="text-sm text-gray-500 mt-6">
                    For technical support, contact IT department
                  </p>
                </div>
              </div>

              {/* Right Side - Company Logo */}
              <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary to-secondary items-center justify-center p-8 lg:p-16">
                <div className="text-center text-white">
                  <img
                    src="/images/qicino-main-logo.svg"
                    alt="Qicino Limited Logo"
                    className="w-48 h-48 lg:w-64 lg:h-64 mx-auto mb-8"
                  />
                  <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                    Qicino Limited
                  </h3>
                  <p className="text-lg lg:text-xl opacity-90">
                    Innovation • Excellence • Growth
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
